import React, { useState, useRef, useEffect, useCallback } from "react";
import { Controller, Control } from "react-hook-form";
import { get } from "react-hook-form";
import { t } from "i18next";
import { REQUIRED_VALIDATOR, PHONE_VALIDATOR } from "../../../../messages/errorsMessages";
import FlagImage from "../FlagImage/FlagImage";
import { DIAL_CODES, DialCode } from "./dialCodes";
import "./EAPhoneInput.scss";

const PHONE_DIGITS_PATTERN = /^\d{9}$/;
const MAX_INTERNATIONAL_PHONE_DIGITS = 15; // E.164 max length (without +)

const DEFAULT_COUNTRY = DIAL_CODES[0];

const getDialEntry = (code: string): DialCode =>
  DIAL_CODES.find((d) => d.code === code) ?? DEFAULT_COUNTRY;

const parseFieldValue = (value: string): { entry: DialCode; digits: string } => {
  if (!value) return { entry: DEFAULT_COUNTRY, digits: "" };

  // Try to find a matching dial code prefix (longest match first to avoid +3 matching +39)
  const sorted = [...DIAL_CODES].sort((a, b) => b.dialCode.length - a.dialCode.length);
  const match = sorted.find((d) => value.startsWith(d.dialCode));
  if (match) {
    return { entry: match, digits: value.slice(match.dialCode.length) };
  }
  return { entry: DEFAULT_COUNTRY, digits: value };
};

const buildCombinedValue = (entry: DialCode, digits: string): string =>
  entry.code === "PL" ? digits : entry.dialCode + digits;

interface IProps {
  control: Control<any>;
  name: string;
  errors: any;
  label: string;
  required?: boolean;
  fullWidth?: boolean;
}

const EAPhoneInput: React.FC<IProps> = ({
  control,
  name,
  errors,
  label,
  required = true,
  fullWidth = false,
}) => {
  const [selectedEntry, setSelectedEntry] = useState<DialCode>(DEFAULT_COUNTRY);
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const keyBufferRef = useRef<{ key: string; matchIndex: number }>({ key: "", matchIndex: 0 });

  // Refs to always have current values inside event handlers / render closures
  const selectedEntryRef = useRef<DialCode>(selectedEntry);
  selectedEntryRef.current = selectedEntry;
  const currentDigitsRef = useRef<string>("");
  const onChangeRef = useRef<(value: string) => void>(() => {});

  const handleCountrySelect = (entry: DialCode) => {
    setSelectedEntry(entry);
    setIsOpen(false);
    onChangeRef.current(buildCombinedValue(entry, currentDigitsRef.current));
  };

  const handleToggleDropdown = () => setIsOpen((prev) => !prev);

  const handleDropdownKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!isOpen || !listRef.current) return;
      const key = e.key.toUpperCase();
      if (key.length !== 1 || !/[A-Z]/.test(key)) return;

      const matches = DIAL_CODES.filter((d) => d.code.startsWith(key));
      if (matches.length === 0) return;

      const buf = keyBufferRef.current;
      if (buf.key !== key) {
        buf.key = key;
        buf.matchIndex = 0;
      } else {
        buf.matchIndex = (buf.matchIndex + 1) % matches.length;
      }

      const target = matches[buf.matchIndex];
      const li = listRef.current.querySelector<HTMLLIElement>(`[data-code="${target.code}"]`);
      if (li) li.scrollIntoView({ block: "nearest" });
    },
    [isOpen]
  );

  useEffect(() => {
    document.addEventListener("keydown", handleDropdownKeyDown);
    return () => document.removeEventListener("keydown", handleDropdownKeyDown);
  }, [handleDropdownKeyDown]);

  const handleClickOutside = useCallback((e: MouseEvent) => {
    if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
      setIsOpen(false);
    }
  }, []);

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [handleClickOutside]);

  const fieldError = get(errors, name);

  return (
    <div className={`ea-phone-input${fullWidth ? " full-width" : ""}`}>
      <label className="ea-phone-input__label">{label}</label>
      <div className={`ea-phone-input__wrapper${fieldError ? " error" : ""}`}>
        <div className="ea-phone-input__country-trigger-wrapper" ref={dropdownRef}>
          <button
            type="button"
            className="ea-phone-input__country-trigger"
            onClick={handleToggleDropdown}
            aria-label="Wybierz kod kraju"
            aria-expanded={isOpen}
          >
            <FlagImage code={selectedEntry.code} />
            <span className="ea-phone-input__country-code">{selectedEntry.dialCode}</span>
            <span className="ea-phone-input__chevron">{isOpen ? "▲" : "▼"}</span>
          </button>

          {isOpen && (
            <ul ref={listRef} className="ea-phone-input__dropdown-list" role="listbox">
              {DIAL_CODES.map((entry) => (
                <li
                  key={entry.code}
                  data-code={entry.code}
                  role="option"
                  aria-selected={entry.code === selectedEntry.code}
                  className={`ea-phone-input__dropdown-item${entry.code === selectedEntry.code ? " selected" : ""}`}
                  onMouseDown={() => handleCountrySelect(entry)}
                >
                  <FlagImage code={entry.code} />
                  <span className="ea-phone-input__dropdown-item-code">{entry.code}</span>
                  <span className="ea-phone-input__dropdown-item-dial">{entry.dialCode}</span>
                </li>
              ))}
            </ul>
          )}
          <div className="ea-phone-input__divider" />
        </div>

        <Controller
          control={control}
          name={name}
          rules={{
            required: required ? REQUIRED_VALIDATOR(t) : false,
            validate: (value: string) => {
              const { digits } = parseFieldValue(value ?? "");
              const { entry } = parseFieldValue(value ?? "");

              if (entry.code === "PL") {
                if (!PHONE_DIGITS_PATTERN.test(digits)) {
                  return PHONE_VALIDATOR(t);
                }
              } else {
                if (digits.length > MAX_INTERNATIONAL_PHONE_DIGITS) {
                  return PHONE_VALIDATOR(t);
                }
              }
              return true;
            },
          }}
          render={({ field }) => {
            const { entry, digits } = parseFieldValue(field.value ?? "");

            // Sync refs so event handlers always see current values
            onChangeRef.current = field.onChange;
            currentDigitsRef.current = digits;

            // Sync selected country if field value changed externally (e.g. form reset)
            if (entry.code !== selectedEntryRef.current.code) {
              setSelectedEntry(getDialEntry(entry.code));
            }

            return (
              <input
                type="text"
                inputMode="numeric"
                className="ea-phone-input__input"
                placeholder="123456789"
                aria-label={label}
                value={digits}
                onChange={(e) => {
                  const numeric = e.target.value.replace(/\D/g, "");
                  const limit = selectedEntryRef.current.code === "PL" ? 9 : MAX_INTERNATIONAL_PHONE_DIGITS;
                  const raw = numeric.slice(0, limit);
                  currentDigitsRef.current = raw;
                  field.onChange(buildCombinedValue(selectedEntryRef.current, raw));
                }}
                onBlur={field.onBlur}
                name={field.name}
              />
            );
          }}
        />
      </div>
      {fieldError && (
        <p className="ea-phone-input__helper-text error">{fieldError.message}</p>
      )}
    </div>
  );
};

export default EAPhoneInput;
